import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faLock } from '@fortawesome/free-solid-svg-icons';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useTranslation } from '../contexts/LanguageContext';
import './StripePaymentForm.css';

/**
 * StripePaymentForm - Stripe card payment form component
 *
 * Uses Stripe Elements to collect card information securely.
 * Handles payment intent confirmation and displays loading/error states.
 */
function StripePaymentForm({
    purchaseType,
    amount,
    onSuccess,
    onError,
    onCancel,
    loading
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [withdrawalConsent, setWithdrawalConsent] = useState(false);
    const { isDarkMode } = useDarkMode();
    const { t, locale } = useTranslation();

    const cardElementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: isDarkMode ? '#f0f0f0' : '#333',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                '::placeholder': {
                    color: isDarkMode ? '#8a8a9a' : '#aab7c4',
                },
            },
            invalid: {
                color: '#e74c3c',
                iconColor: '#e74c3c',
            },
        },
        hidePostalCode: true,
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            setError(t('store.errors.stripeNotLoaded'));
            return;
        }

        if (processing || loading) {
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            // Get the CardElement
            const cardElement = elements.getElement(CardElement);

            if (!cardElement) {
                throw new Error(t('store.errors.cardUnavailable'));
            }

            // Call parent's success handler to get payment intent
            // The parent will create the payment intent and pass it back
            await onSuccess(stripe, elements, cardElement);

        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || t('store.errors.paymentFailed'));
            if (onError) {
                onError(err);
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleCardChange = (event) => {
        if (event.error) {
            setError(event.error.message);
        } else {
            setError(null);
        }
    };

    const formatAmount = (cents) => {
        return (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });
    };

    const isLoading = processing || loading;

    return (
        <form onSubmit={handleSubmit} className="stripe-payment-form">
            <div className="payment-amount">
                <span className="amount-label">{t('store.payment.total')}</span>
                <span className="amount-value">{formatAmount(amount)}</span>
                <span className="vat-included">{t('store.payment.vatIncluded')}</span>
            </div>

            <div className="card-element-container">
                <label htmlFor="card-element" className="card-label">
                    {t('store.payment.cardDetails')}
                </label>
                <div className="card-element-wrapper">
                    <CardElement
                        id="card-element"
                        options={cardElementOptions}
                        onChange={handleCardChange}
                    />
                </div>
            </div>

            {error && (
                <div className="payment-error-message" role="alert">
                    {error}
                </div>
            )}

            {process.env.NODE_ENV !== 'production' && (
                <div className="payment-test-info">
                    <strong>Test Mode:</strong> Use card <code>4242 4242 4242 4242</code>
                    <br />
                    Any future date, any CVC, any ZIP
                </div>
            )}

            <div className="withdrawal-consent">
                <label className="withdrawal-consent-label">
                    <input
                        type="checkbox"
                        checked={withdrawalConsent}
                        onChange={(e) => setWithdrawalConsent(e.target.checked)}
                    />
                    <span>{t('store.payment.withdrawalConsent')}</span>
                </label>
            </div>

            <div className="payment-actions">
                <button
                    type="button"
                    className="btn-cancel"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    {t('common.cancel')}
                </button>
                <button
                    type="submit"
                    className="btn-pay"
                    disabled={!stripe || isLoading || !withdrawalConsent}
                >
                    {isLoading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>{t('store.payment.processing')}</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faLock} />
                            <span>{t('store.payment.pay', { amount: formatAmount(amount) })}</span>
                        </>
                    )}
                </button>
            </div>

            <div className="payment-security">
                <FontAwesomeIcon icon={faLock} />
                <span>{t('store.payment.securedBy')}</span>
            </div>
        </form>
    );
}

export default StripePaymentForm;
